import { UseQueryOptionsNoKeyAndFn } from "@/common/api/_types";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { readUrlStateSlice, withUrlStateSlice } from "@/common/helpers/url-state";
import { useDevice } from "@/common/hooks/use-device";
import { filterVisibleColumns } from "@/components/data-table/_helpers/filter-visible-columns";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableHeightMode, DataTableRow, ImplementedColumnDef } from "@/components/data-table/types";
import { CommandButton } from "@/components/command-palette/command-button";
import { RowActionVariant, type RowAction } from "@/components/ui/table-row-actions";
import { userConfigStore } from "@/store/user-config-store";
import { EditIcon, EyeIcon, PlusCircleIcon, RotateCcwIcon, Trash2Icon } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "wouter";
import {
  BasePredefinedTableProps,
  BaseQueryParams,
  BaseSorting,
  DataFetchHooks,
  PredefinedTableConfig,
  RowActionHelpers,
  TableHooks,
} from "./base-predefined-table.types";
import { DataExportButton } from "./data-export-button";

export interface TableState {
  search?: string;
  page?: number;
  pageSize?: number;
  orderBy?: Array<{ field: string; direction: "asc" | "desc" }>;
  filters?: Record<string, string[]>;
}

export function createFilterBody<TRequestBody>(body: TRequestBody, filterValues: Record<string, string[]> | undefined) {
  if (!filterValues) {
    return body;
  }

  const cleanedFilters: Record<string, string[]> = {};
  for (const [key, values] of Object.entries(filterValues)) {
    if (values?.length > 0) {
      cleanedFilters[key] = values;
    }
  }

  return { ...(body as Record<string, unknown>), ...cleanedFilters } as TRequestBody;
}

export function buildTableState(
  search: string,
  page: number,
  pageSize: number,
  defaultPageSize: number,
  orderBy: BaseSorting | null,
  defaultOrderBy: BaseSorting | null,
  filters: Record<string, string[]>,
  defaultFilters?: Record<string, string[]>,
): TableState | undefined {
  const state: TableState = {};

  if (search) state.search = search;
  if (page && page !== 1) state.page = page;
  if (pageSize !== defaultPageSize) state.pageSize = pageSize;

  const isOrderByDefault =
    (!orderBy && !defaultOrderBy) ||
    (orderBy && defaultOrderBy && JSON.stringify(orderBy) === JSON.stringify(defaultOrderBy));

  if (orderBy?.length && !isOrderByDefault) {
    state.orderBy = orderBy.map((sort) => ({ field: sort.field, direction: sort.direction }));
  }

  if (filters) {
    const filtersToSave: Record<string, string[]> = {};

    for (const [key, values] of Object.entries(filters)) {
      const defaultValue = defaultFilters?.[key];
      const currentValue = values || [];
      const isDifferentFromDefault = !defaultValue || JSON.stringify(currentValue) !== JSON.stringify(defaultValue);

      if (isDifferentFromDefault) {
        filtersToSave[key] = currentValue;
      }
    }

    if (Object.keys(filtersToSave).length > 0) {
      state.filters = filtersToSave;
    }
  }

  if (Object.keys(state).length === 0) return undefined;

  return state;
}

export function getEffectiveRowActionConfig<
  TData extends RowWithIdentifier,
  TSorting extends BaseSorting,
  TExtend = unknown,
  TSelect = unknown,
  TQueryParams extends BaseQueryParams<TExtend, TSelect> = BaseQueryParams<TExtend, TSelect>,
  TRequestBody = unknown,
>(
  isDeleted: boolean,
  defaults: { editLabel: string; viewLabel: string; deleteLabel: string; restoreLabel: string },
  configDefaultActions: PredefinedTableConfig<TSorting>["defaultRowActions"] = {},
  propDefaultActions: BasePredefinedTableProps<
    TData,
    TSorting,
    TQueryParams,
    TRequestBody,
    TExtend,
    TSelect
  >["defaultRowActions"] = {},
) {
  const baseActions = {
    showEdit: !isDeleted,
    showView: true,
    showDelete: true,
    showRestore: isDeleted,
    ...defaults,
  };

  const merged = {
    ...baseActions,
    ...configDefaultActions,
    ...propDefaultActions,
  };

  if (isDeleted && propDefaultActions?.showEdit === undefined) {
    merged.showEdit = false;
  }

  return merged;
}

export function createBasePredefinedTable<
  TData extends RowWithIdentifier,
  TSorting extends BaseSorting,
  TExtend = unknown,
  TSelect = unknown,
  TQueryParams extends BaseQueryParams<TExtend, TSelect> = BaseQueryParams<TExtend, TSelect>,
  TRequestBody = unknown,
>({
  config,
  queryHooks,
  tableHooks = {},
  defaultColumns,
}: {
  config: PredefinedTableConfig<TSorting>;
  queryHooks: DataFetchHooks<TData, TQueryParams, TRequestBody>;
  tableHooks?: TableHooks<TData>;
  defaultColumns: unknown[];
}) {
  return function BasePredefinedTable(
    props: BasePredefinedTableProps<TData, TSorting, TQueryParams, TRequestBody, TExtend, TSelect>,
  ) {
    const { t } = useTranslation(["common"]);
    userConfigStore((state) => state.dateFormat);

    const {
      autoFetch = true,
      query,
      body,
      data,
      isLoading,
      isError,
      totalItems,

      // Table configuration
      columns,
      features,
      searchPlaceholder = config.defaultTableConfig.searchPlaceholder,
      SearchComponent,
      page = 1,
      pageSize = config.defaultPageSize,
      initialSorting,
      defaultSorting = config.defaultSorting,
      serverSidePagination = true,
      sortingMapping = config.sortingMapping ?? {},
      heightMode = DataTableHeightMode.Content,

      // DataTable styling
      className,
      tableBackground,
      tableClassName,

      // UI customization
      headerComponent,
      filterContainerClassName,
      filterContainerCustomContent,
      filterContainerCustomContentPosition,
      createButtonText = t("common:Add"),
      createButtonLink = "/create",
      showExportButton = false,
      showCreateButton = false,
      footerComponent,

      // Action handlers
      onRowClick,
      onRowNavigate,
      onEdit,
      onView,
      onDelete,
      onRestore,
      customRowActions,
      customSelectionActions,

      // Default row actions configuration
      defaultRowActions,

      // Custom row actions
      confirmActions,
      confirmLabels,

      // External control
      onPaginationChange,
      onSortingChange,
      onSearchChange,
      onSelectionChange,
      onClearSelection,
      onFilterChange,

      // Selection state
      showClearSelection = true,
      showSelectionCount = true,
      selection,
      selectionMode = "uuid",
      identifierProperty = "uuid",
      rowSelection,
      setRowSelection,

      // Filter support
      filters: filtersToDisplay,
      filterValues,

      // Expansion support
      subComponent,
      getRowCanExpand,
      getRowCanSelect,

      // Meta support
      meta,

      // Data extensions
      extend,

      // Query options
      queryOptions,

      // Empty state customization
      noDataTitle,
      noDataDescription,
      noDataIcon,
      noResultsTitle,
      noResultsDescription,

      // Toast messages
      toastMessages = config.defaultToastMessages,

      // Unique table name for URL state
      name,

      // Disable URL persistence
      disableUrlPersistence = false,
    } = props;

    const effectiveGetRowCanExpand = useMemo((): ((item: TData) => boolean) | undefined => {
      if (getRowCanExpand) {
        return getRowCanExpand;
      }

      if (subComponent) {
        return () => true;
      }

      return undefined;
    }, [getRowCanExpand, subComponent]);

    const [_, navigate] = useLocation();
    const { deviceType } = useDevice();
    const [searchParams, setSearchParams] = useSearchParams();

    const paramKey = name ?? "table";
    const urlState = disableUrlPersistence ? null : (readUrlStateSlice<TableState>(searchParams, paramKey) ?? null);

    const defaultFilterValuesRef = useRef(filterValues ?? {});
    const defaultFilterValues = defaultFilterValuesRef.current;
    const lastSliceJsonRef = useRef<string | undefined>(undefined);
    const [loadStateFromUrl, setLoadStateFromUrl] = useState(true);
    const [internalPaginationState, setInternalPaginationState] = useState({
      page: urlState?.page ?? query?.page ?? page,
      pageSize: urlState?.pageSize ?? query?.pageSize ?? pageSize,
    });
    const [internalSearchValue, setInternalSearchValue] = useState(urlState?.search ?? query?.search ?? "");
    const [internalSorting, setInternalSorting] = useState<TSorting | null>(
      (urlState?.orderBy?.map((s) => ({ field: s.field, direction: s.direction })) ??
        query?.orderBy ??
        defaultSorting) as TSorting,
    );
    const [internalFilterValues, setInternalFilterValues] = useState<Record<string, string[]>>(() => {
      const merged: Record<string, string[]> = { ...(filterValues ?? {}) };

      if (urlState?.filters) {
        for (const [key, values] of Object.entries(urlState.filters)) {
          merged[key] = values;
        }
      }

      return merged;
    });

    const shouldUseQuery = autoFetch && !data;

    const countContextKey = JSON.stringify({
      search: internalSearchValue || undefined,
      filters: internalFilterValues,
      body,
      query,
    });
    const totalCacheRef = useRef<{ key: string; total: number } | null>(null);
    const cachedTotal = totalCacheRef.current?.key === countContextKey ? totalCacheRef.current.total : undefined;

    const countContextKeyRef = useRef(countContextKey);
    countContextKeyRef.current = countContextKey;

    const [skipCountForContextKey, setSkipCountForContextKey] = useState<string | null>(null);
    const includeCount = skipCountForContextKey !== countContextKey;

    const skipCountForCurrentContext = useCallback(() => {
      if (totalCacheRef.current?.key === countContextKeyRef.current) {
        setSkipCountForContextKey(countContextKeyRef.current);
      }
    }, []);

    const {
      data: searchData,
      isPending: searchIsPending,
      error: searchError,
    } = queryHooks.useSearchQuery({
      options: shouldUseQuery
        ? {
            parameters: {
              search: internalSearchValue || undefined,
              page: internalPaginationState.page,
              pageSize: internalPaginationState.pageSize,
              orderBy: internalSorting ?? undefined,
              extend,
              ...(includeCount ? {} : { includeCount: false }),
              ...query,
            } as TQueryParams,
            body: createFilterBody(body, internalFilterValues) as TRequestBody,
          }
        : undefined,
      enabled: shouldUseQuery,
      queryOptions: queryOptions as UseQueryOptionsNoKeyAndFn,
    });

    const deleteMutation = queryHooks.useDeleteMutation?.();
    const restoreMutation = queryHooks.useRestoreMutation?.();

    const responseTotal = searchData?.meta?.total;

    useEffect(() => {
      if (responseTotal !== undefined && responseTotal >= 0) {
        totalCacheRef.current = { key: countContextKey, total: responseTotal };
      }
    }, [responseTotal, countContextKey]);

    const tableData = data ?? searchData?.data;
    const tableIsLoading = isLoading ?? searchIsPending;
    const tableIsError = isError ?? !!searchError;
    const tableTotalItems =
      totalItems ?? (responseTotal !== undefined && responseTotal >= 0 ? responseTotal : cachedTotal);

    const invalidateCount = useCallback(() => {
      totalCacheRef.current = null;
      setSkipCountForContextKey(null);
    }, []);
    const rowActionHelpers = useMemo<RowActionHelpers>(() => ({ invalidateCount }), [invalidateCount]);

    useEffect(() => {
      if (!loadStateFromUrl || !urlState) {
        return;
      }

      if ((urlState.page !== undefined || urlState.pageSize !== undefined) && onPaginationChange) {
        onPaginationChange({
          page: urlState.page ?? page,
          pageSize: urlState.pageSize ?? pageSize,
        });
      }

      if (urlState.search !== undefined && onSearchChange) {
        onSearchChange(urlState.search);
      }

      if (urlState.orderBy && onSortingChange) {
        onSortingChange(
          urlState.orderBy.map((sort) => ({
            field: sort.field,
            direction: sort.direction,
          })) as TSorting,
        );
      }

      if (urlState.filters && onFilterChange) {
        for (const [key, values] of Object.entries(urlState.filters)) {
          onFilterChange(key, values);
        }
      }
    }, [
      loadStateFromUrl,
      onFilterChange,
      onPaginationChange,
      onSearchChange,
      onSortingChange,
      page,
      pageSize,
      urlState,
    ]);

    useEffect(() => {
      if (loadStateFromUrl) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect -- one-time flag that must flip after the initial URL-state effects ran
        setLoadStateFromUrl(false);
      }
    }, [loadStateFromUrl]);

    useEffect(() => {
      if (disableUrlPersistence) return;

      if (!loadStateFromUrl) {
        const state = buildTableState(
          internalSearchValue,
          internalPaginationState.page,
          internalPaginationState.pageSize,
          pageSize,
          internalSorting,
          defaultSorting as TSorting,
          internalFilterValues,
          defaultFilterValues,
        );
        const sliceJson = state ? JSON.stringify(state) : undefined;

        if (sliceJson !== lastSliceJsonRef.current) {
          lastSliceJsonRef.current = sliceJson;

          setSearchParams((currentParams) => withUrlStateSlice(currentParams, paramKey, state));
        }
      }
    }, [
      loadStateFromUrl,
      internalPaginationState,
      internalSearchValue,
      internalSorting,
      internalFilterValues,
      pageSize,
      setSearchParams,
      paramKey,
      defaultFilterValues,
      defaultSorting,
      disableUrlPersistence,
    ]);

    useEffect(() => {
      if (filterValues) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect -- filterValues prop must override the URL-merged initial filters after mount
        setInternalFilterValues(filterValues);
      }
    }, [filterValues]);

    useEffect(() => {
      if (disableUrlPersistence || loadStateFromUrl) return;

      const currentUrlState = readUrlStateSlice<TableState>(searchParams, paramKey) ?? null;
      const sliceJson = currentUrlState ? JSON.stringify(currentUrlState) : undefined;
      if (sliceJson === lastSliceJsonRef.current) return;

      lastSliceJsonRef.current = sliceJson;

      const newFilters = { ...defaultFilterValues, ...currentUrlState?.filters };
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- syncs state from the URL (external source)
      setInternalFilterValues(newFilters);

      // eslint-disable-next-line @eslint-react/set-state-in-effect -- syncs state from the URL (external source)
      setInternalPaginationState({
        page: currentUrlState?.page ?? 1,
        pageSize: currentUrlState?.pageSize ?? pageSize,
      });

      const newSorting = currentUrlState?.orderBy
        ? (currentUrlState.orderBy.map((s) => ({ field: s.field, direction: s.direction })) as TSorting)
        : (defaultSorting as TSorting);
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- syncs state from the URL (external source)
      setInternalSorting(newSorting);
      if (onSortingChange) {
        onSortingChange(newSorting);
      }

      if (onFilterChange) {
        for (const [key, values] of Object.entries(newFilters)) {
          onFilterChange(key, values);
        }
      }

      const newSearchValue = currentUrlState?.search ?? "";
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- syncs state from the URL (external source)
      setInternalSearchValue(newSearchValue);
      if (onSearchChange) {
        onSearchChange(newSearchValue);
      }
    }, [
      searchParams,
      paramKey,
      loadStateFromUrl,
      defaultFilterValues,
      onFilterChange,
      onSortingChange,
      pageSize,
      onSearchChange,
      defaultSorting,
      disableUrlPersistence,
    ]);

    const tableInitialSorting = useMemo(() => {
      const sortingToConvert = initialSorting ?? internalSorting ?? defaultSorting;

      if (!sortingMapping || Object.keys(sortingMapping).length === 0) {
        return sortingToConvert.map((sort) => ({
          id: sort.field,
          desc: sort.direction === "desc",
        }));
      }

      const resultSorting: { id: string; desc: boolean }[] = [];
      const sortedMappings = Object.entries(sortingMapping).sort((a, b) => b[1].length - a[1].length);

      let currentIndex = 0;
      while (currentIndex < sortingToConvert.length) {
        let matchFound = false;

        for (const [uiField, backendFields] of sortedMappings) {
          if (backendFields.length === 0) continue;

          if (currentIndex + backendFields.length > sortingToConvert.length) continue;

          let isMatch = true;
          const firstDirection = sortingToConvert[currentIndex].direction;

          for (let i = 0; i < backendFields.length; i++) {
            const sortItem = sortingToConvert[currentIndex + i];
            if (sortItem.field !== backendFields[i] || sortItem.direction !== firstDirection) {
              isMatch = false;
              break;
            }
          }

          if (isMatch) {
            resultSorting.push({
              id: uiField,
              desc: firstDirection === "desc",
            });
            currentIndex += backendFields.length;
            matchFound = true;
            break;
          }
        }

        if (!matchFound) {
          const sortItem = sortingToConvert[currentIndex];
          resultSorting.push({
            id: sortItem.field,
            desc: sortItem.direction === "desc",
          });
          currentIndex++;
        }
      }

      return resultSorting;
    }, [initialSorting, internalSorting, defaultSorting, sortingMapping]);

    const handleDelete = useCallback(
      async (item: TData) => {
        if (onDelete) {
          return onDelete(item);
        }

        const isDeleted = !!item.deletedAt;

        if (deleteMutation && toastMessages) {
          const successMessage = isDeleted
            ? t("common:ElementPermanentlyDeleted")
            : (toastMessages.deleteSuccess ?? t("common:ElementDeleted"));

          const errorMessage = isDeleted
            ? t("common:ErrorPermanentlyDeletingElement")
            : (toastMessages.deleteError ?? t("common:ErrorDeletingElement"));

          return await toastMutationStatus({
            mutationFn: async () => {
              const params = isDeleted
                ? { uuid: item[identifierProperty], force: true }
                : { uuid: item[identifierProperty] };
              const result = await deleteMutation.mutateAsync(params);
              invalidateCount();
              return result;
            },
            successMessage,
            errorMessage,
            customErrors: toastMessages.deleteCustomErrors,
            resolveErrorMessage: toastMessages.deleteResolveErrorMessage,
          });
        }

        throw new Error("No delete handler available");
      },
      [onDelete, deleteMutation, toastMessages, identifierProperty, t, invalidateCount],
    );

    const handleRestore = useCallback(
      async (item: TData) => {
        if (onRestore) {
          return onRestore(item);
        }

        if (restoreMutation && toastMessages) {
          return await toastMutationStatus({
            mutationFn: async () => {
              const result = await restoreMutation.mutateAsync({ uuid: item[identifierProperty] });
              invalidateCount();
              return result;
            },
            successMessage: toastMessages.restoreSuccess ?? t("common:ElementHasBeenRestored"),
            errorMessage: toastMessages.restoreError ?? t("common:ErrorRestoringElement"),
          });
        }

        throw new Error("No restore handler available");
      },
      [onRestore, restoreMutation, toastMessages, identifierProperty, t, invalidateCount],
    );

    const defaultEditHandler = useCallback(
      (item: TData) => {
        const uuid = item.uuid || item.id;
        if (uuid) {
          navigate(`/${uuid}/update`);
        }
      },
      [navigate],
    );

    const defaultViewHandler = useCallback(
      (item: TData) => {
        const uuid = item.uuid || item.id;
        if (uuid) {
          navigate(`/${uuid}`);
        }
      },
      [navigate],
    );

    const effectiveOnEdit = onEdit || defaultEditHandler;
    const effectiveOnView = onView || defaultViewHandler;

    const effectiveOnRowClickFn = useMemo(() => {
      if (onRowClick) return onRowClick;
      if (onRowNavigate) {
        return (item: TData) => {
          const url = onRowNavigate(item);
          if (url) navigate(url);
        };
      }
      return undefined;
    }, [onRowClick, onRowNavigate, navigate]);

    const createRowActions = useCallback(
      (row: DataTableRow<TData>) => {
        const rowActions: RowAction[] = customRowActions ? customRowActions(row, rowActionHelpers) : [];
        const isDeleted = !!row.original.deletedAt;

        const effectiveRowActions = getEffectiveRowActionConfig(
          isDeleted,
          {
            editLabel: t("common:Edit"),
            viewLabel: t("common:View"),
            deleteLabel: isDeleted ? t("common:DeletePermanently") : t("ram:Delete"),
            restoreLabel: t("common:Restore"),
          },
          config.defaultRowActions,
          defaultRowActions,
        );

        if (effectiveRowActions.showView) {
          const viewLabels =
            typeof confirmLabels?.view === "function" ? confirmLabels.view(row.original) : confirmLabels?.view;

          const displayViewLabel =
            typeof effectiveRowActions.viewLabel === "string" && effectiveRowActions.viewLabel.includes(":")
              ? t(effectiveRowActions.viewLabel)
              : effectiveRowActions.viewLabel;

          const displayViewConfirmLabels = viewLabels
            ? {
                title: viewLabels.title?.includes(":") ? t(viewLabels.title) : viewLabels.title,
                text: viewLabels.text?.includes(":") ? t(viewLabels.text) : viewLabels.text,
                confirmButton: viewLabels.confirmButton?.includes(":")
                  ? t(viewLabels.confirmButton)
                  : viewLabels.confirmButton,
                cancelButton: viewLabels.cancelButton?.includes(":")
                  ? t(viewLabels.cancelButton)
                  : viewLabels.cancelButton,
              }
            : undefined;

          rowActions.push({
            label: displayViewLabel,
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => effectiveOnView(row.original),
            needsConfirm: !!confirmActions?.includes("view"),
            ...(displayViewConfirmLabels && { confirmLabels: displayViewConfirmLabels }),
          });
        }

        if (effectiveRowActions.showEdit) {
          const editLabels =
            typeof confirmLabels?.edit === "function" ? confirmLabels.edit(row.original) : confirmLabels?.edit;

          const displayEditLabel =
            typeof effectiveRowActions.editLabel === "string" && effectiveRowActions.editLabel.includes(":")
              ? t(effectiveRowActions.editLabel)
              : effectiveRowActions.editLabel;

          const displayEditConfirmLabels = editLabels
            ? {
                title: editLabels.title?.includes(":") ? t(editLabels.title) : editLabels.title,
                text: editLabels.text?.includes(":") ? t(editLabels.text) : editLabels.text,
                confirmButton: editLabels.confirmButton?.includes(":")
                  ? t(editLabels.confirmButton)
                  : editLabels.confirmButton,
                cancelButton: editLabels.cancelButton?.includes(":")
                  ? t(editLabels.cancelButton)
                  : editLabels.cancelButton,
              }
            : undefined;

          rowActions.push({
            label: displayEditLabel,
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => effectiveOnEdit(row.original),
            needsConfirm: !!confirmActions?.includes("edit"),
            ...(displayEditConfirmLabels && { confirmLabels: displayEditConfirmLabels }),
          });
        }

        if (effectiveRowActions.showRestore && (onRestore || restoreMutation)) {
          const restoreLabels =
            typeof confirmLabels?.restore === "function" ? confirmLabels.restore(row.original) : confirmLabels?.restore;

          const displayRestoreLabel =
            typeof effectiveRowActions.restoreLabel === "string" && effectiveRowActions.restoreLabel.includes(":")
              ? t(effectiveRowActions.restoreLabel)
              : effectiveRowActions.restoreLabel;

          const displayRestoreConfirmLabels = restoreLabels
            ? {
                title: restoreLabels.title?.includes(":") ? t(restoreLabels.title) : restoreLabels.title,
                text: restoreLabels.text?.includes(":") ? t(restoreLabels.text) : restoreLabels.text,
                confirmButton: restoreLabels.confirmButton?.includes(":")
                  ? t(restoreLabels.confirmButton)
                  : restoreLabels.confirmButton,
                cancelButton: restoreLabels.cancelButton?.includes(":")
                  ? t(restoreLabels.cancelButton)
                  : restoreLabels.cancelButton,
              }
            : undefined;

          rowActions.push({
            label: displayRestoreLabel,
            icon: <RotateCcwIcon className="h-4 w-4" />,
            onClick: () => handleRestore(row.original),
            needsConfirm: !!confirmActions?.includes("restore"),
            ...(displayRestoreConfirmLabels
              ? { confirmLabels: displayRestoreConfirmLabels }
              : {
                  confirmLabels: {
                    title: t("common:ConfirmRestore"),
                    text: t("common:DoYouReallyWantToRestoreThisElement"),
                    confirmButton: t("common:Restore"),
                    cancelButton: t("common:Cancel"),
                  },
                }),
          });
        }

        if (effectiveRowActions.showDelete && (onDelete || deleteMutation)) {
          const deleteLabels =
            typeof confirmLabels?.delete === "function" ? confirmLabels.delete(row.original) : confirmLabels?.delete;

          const displayDeleteLabel =
            typeof effectiveRowActions.deleteLabel === "string" && effectiveRowActions.deleteLabel.includes(":")
              ? t(effectiveRowActions.deleteLabel)
              : effectiveRowActions.deleteLabel;

          const displayDeleteConfirmLabels = deleteLabels
            ? {
                title: deleteLabels.title?.includes(":") ? t(deleteLabels.title) : deleteLabels.title,
                text: deleteLabels.text?.includes(":") ? t(deleteLabels.text) : deleteLabels.text,
                confirmButton: deleteLabels.confirmButton?.includes(":")
                  ? t(deleteLabels.confirmButton)
                  : deleteLabels.confirmButton,
                cancelButton: deleteLabels.cancelButton?.includes(":")
                  ? t(deleteLabels.cancelButton)
                  : deleteLabels.cancelButton,
              }
            : undefined;

          rowActions.push({
            label: displayDeleteLabel,
            icon: <Trash2Icon className="h-4 w-4" />,
            variant: RowActionVariant.Error,
            onClick: () => handleDelete(row.original),
            needsConfirm: !!confirmActions?.includes("delete"),
            ...(displayDeleteConfirmLabels
              ? { confirmLabels: displayDeleteConfirmLabels }
              : {
                  confirmLabels: {
                    title: isDeleted ? t("common:DeletePermanently") : t("common:ConfirmDelete"),
                    text: isDeleted
                      ? t("common:DeleteItemPermanentlyFromDatabaseCannotBeUndone")
                      : t("common:ThisActionCannotBeUndone"),
                    confirmButton: isDeleted ? t("common:YesDeletePermanently") : t("ram:Delete"),
                    cancelButton: t("common:Cancel"),
                  },
                }),
          });
        }

        return rowActions;
      },
      [
        t,
        effectiveOnEdit,
        effectiveOnView,
        onDelete,
        deleteMutation,
        onRestore,
        restoreMutation,
        customRowActions,
        rowActionHelpers,
        handleDelete,
        handleRestore,
        defaultRowActions,
        confirmActions,
        confirmLabels,
      ],
    );

    const tableActions = useMemo(
      () => ({
        onPaginationChange: (pagination: { page: number; pageSize: number }) => {
          skipCountForCurrentContext();
          setInternalPaginationState(pagination);

          if (onPaginationChange) {
            onPaginationChange(pagination);
          }
        },
        onSortingChange: (
          sorting: Array<{
            field: string;
            direction: "asc" | "desc";
          }>,
        ) => {
          const typedSorting = sorting.map((s) => ({
            field: s.field,
            direction: s.direction,
          })) as TSorting;
          skipCountForCurrentContext();
          setInternalSorting(typedSorting);

          if (onSortingChange) {
            onSortingChange(typedSorting);
          }
        },
        onSearchChange: (value: string) => {
          setSkipCountForContextKey(null);
          setInternalSearchValue(value);

          if (onSearchChange) {
            onSearchChange(value);
          }
        },
        onSelectionChange: onSelectionChange,
        onClearSelection: onClearSelection,
        onRowClick: effectiveOnRowClickFn
          ? (row: DataTableRow<TData>) => effectiveOnRowClickFn(row.original, row)
          : undefined,
        onRowNavigate: onRowNavigate ? (row: DataTableRow<TData>) => onRowNavigate(row.original) : undefined,
        onFilterChange: (filterId: string, values: string[]) => {
          setSkipCountForContextKey(null);
          setInternalFilterValues((prev) => ({
            ...prev,
            [filterId]: values,
          }));

          if (onFilterChange) {
            onFilterChange(filterId, values);
          }
        },
      }),
      [
        onPaginationChange,
        onSortingChange,
        onSearchChange,
        effectiveOnRowClickFn,
        onRowNavigate,
        onSelectionChange,
        onClearSelection,
        onFilterChange,
        skipCountForCurrentContext,
      ],
    );

    const exportQuery = useMemo(
      () => ({ search: internalSearchValue || undefined, orderBy: internalSorting ?? undefined, ...query }),
      [internalSearchValue, internalSorting, query],
    );
    const exportBody = useMemo(() => createFilterBody(body, internalFilterValues), [body, internalFilterValues]);
    const exportColumns = useMemo(
      () =>
        filterVisibleColumns((columns ?? defaultColumns) as ImplementedColumnDef<TData, unknown>[], deviceType).flatMap(
          (column) => column.exportPaths ?? [column.id],
        ),
      [columns, deviceType],
    );

    const tableHeaderComponent = useMemo(() => {
      const exportButton =
        config.exportType && showExportButton ? (
          <DataExportButton
            type={config.exportType}
            query={exportQuery}
            body={exportBody}
            defaultColumns={exportColumns}
          />
        ) : null;

      let mainComponent: ReactNode = null;
      if (headerComponent !== undefined) {
        mainComponent = headerComponent;
      } else if (showCreateButton) {
        mainComponent = (
          <CommandButton
            commandId={`create-${createButtonLink}`}
            label={createButtonText}
            icon={<PlusCircleIcon className="h-4 w-4" />}
            to={createButtonLink}
          />
        );
      }

      if (!exportButton) {
        return mainComponent;
      }
      return (
        <>
          {exportButton}
          {mainComponent}
        </>
      );
    }, [
      headerComponent,
      showCreateButton,
      createButtonLink,
      createButtonText,
      showExportButton,
      exportQuery,
      exportBody,
      exportColumns,
    ]);

    const tableColumns = columns ?? (defaultColumns as ImplementedColumnDef<TData, unknown>[]);
    const tableFeatures = { ...config.defaultTableConfig.features, ...features };
    const tableCustomSelectionActions = customSelectionActions ?? [];

    return (
      <DataTable
        className={className}
        tableClassName={tableClassName}
        tableBackground={tableBackground}
        columns={tableColumns}
        data={tableData}
        isLoading={tableIsLoading}
        isError={tableIsError}
        headerComponent={tableHeaderComponent}
        filterContainerClassName={filterContainerClassName}
        filterContainerCustomContent={filterContainerCustomContent}
        filterContainerCustomContentPosition={filterContainerCustomContentPosition}
        footerComponent={footerComponent}
        actions={tableActions}
        totalItems={tableTotalItems}
        features={tableFeatures}
        serverSidePagination={serverSidePagination}
        initialSorting={tableInitialSorting}
        paginationState={{
          page: internalPaginationState.page,
          pageSize: internalPaginationState.pageSize,
        }}
        filters={filtersToDisplay}
        filterValues={internalFilterValues}
        defaultFilterValues={defaultFilterValues}
        searchValue={internalSearchValue}
        searchPlaceholder={t(String(searchPlaceholder))}
        SearchComponent={SearchComponent}
        rowActions={customRowActions === null ? undefined : createRowActions}
        sortingMapping={sortingMapping}
        customSelectionActions={tableCustomSelectionActions}
        showClearSelection={showClearSelection}
        showSelectionCount={showSelectionCount}
        selection={selection}
        selectionMode={selectionMode}
        identifierProperty={identifierProperty}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        heightMode={heightMode}
        subComponent={subComponent}
        getRowCanExpand={effectiveGetRowCanExpand}
        getRowCanSelect={getRowCanSelect}
        meta={meta}
        groupByCallback={tableHooks?.groupByCallback}
        noDataTitle={noDataTitle}
        noDataDescription={noDataDescription}
        noDataIcon={noDataIcon}
        noResultsTitle={noResultsTitle}
        noResultsDescription={noResultsDescription}
      />
    );
  };
}
